import { useRef, useState } from "react";
import type { Task } from "../../api.ts";
import { tokens, useTheme } from "../../glassTheme.tsx";
import { GlassButton } from "../glass/Button.tsx";
import { TierDot } from "../sessions/Row.tsx";
import { shortDate } from "../../theme.ts";

const THRESHOLD = 72;
const CEILING = 120;

/**
 * Swipe right: done. Swipe left: park (§2). The backend's `parkTask`
 * actually requires a non-empty reason (`if (!because?.trim()) throw
 * "parking needs a reason"`) — the spec's "can be left empty" doesn't hold
 * against the real route, so Park stays disabled until something is typed,
 * same as the flag's Resolve field.
 */
export function BoardRow({
  task, afterTitle, onOpen, onDone, onPark,
}: {
  task: Task; afterTitle?: string | null; onOpen: () => void;
  onDone: () => void; onPark: (because: string) => void;
}) {
  const { colors } = useTheme();
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [parking, setParking] = useState(false);
  const [because, setBecause] = useState("");
  const start = useRef<{ x: number; y: number } | null>(null);
  const intent = useRef<"none" | "horizontal" | "vertical">("none");

  const down = (e: React.TouchEvent) => {
    if (parking) return;
    start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    intent.current = "none";
  };
  const move = (e: React.TouchEvent) => {
    if (!start.current) return;
    const dxRaw = e.touches[0].clientX - start.current.x;
    const dyRaw = e.touches[0].clientY - start.current.y;
    if (intent.current === "none" && Math.abs(dxRaw) + Math.abs(dyRaw) > 6) {
      intent.current = Math.abs(dxRaw) > Math.abs(dyRaw) ? "horizontal" : "vertical";
    }
    if (intent.current !== "horizontal") return;
    setDragging(true);
    setDx(Math.max(-CEILING, Math.min(CEILING, dxRaw)));
    if (e.cancelable) e.preventDefault();
  };
  const up = () => {
    if (intent.current === "horizontal") {
      if (dx > THRESHOLD) onDone();
      else if (dx < -THRESHOLD) setParking(true);
    }
    setDx(0); setDragging(false); start.current = null; intent.current = "none";
  };

  const tint = task.value_id ? (colors.tint as Record<string, string>)[task.value_id] : undefined;
  const dim = task.blocked;
  const meta = dim && afterTitle
    ? `after ${afterTitle}`
    : [task.breadcrumb, task.due ? shortDate(task.due) : null].filter(Boolean).join(" · ");

  return (
    <div style={{ position: "relative" }}>
      <div aria-hidden style={{
        position: "absolute", inset: 0, display: "flex", justifyContent: "space-between",
        alignItems: "center", padding: "0 6px", fontSize: 12.5, color: colors.ink2, pointerEvents: "none",
      }}>
        <span style={{ opacity: dx < -20 ? Math.min(1, -dx / THRESHOLD) : 0 }}>Park</span>
        <span style={{ opacity: dx > 20 ? Math.min(1, dx / THRESHOLD) : 0 }}>Done</span>
      </div>
      <div
        onTouchStart={down} onTouchMove={move} onTouchEnd={up} onTouchCancel={up}
        onClick={() => { if (!dragging) onOpen(); }}
        style={{
          position: "relative", background: colors.sheet,
          transform: `translateX(${dx}px)`, transition: dragging ? "none" : "transform 200ms ease",
          display: "grid", gridTemplateColumns: "3px 1fr", gap: 12, alignItems: "center",
          padding: "12px 4px", borderBottom: `1px solid ${colors.hair}`, cursor: "pointer",
        }}
      >
        <span style={{ width: 3, height: 22, borderRadius: 2, background: tint ?? colors.hair2, justifySelf: "center" }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, color: dim ? colors.ink2 : colors.ink, display: "flex", alignItems: "center" }}>
            <TierDot tier={task.tier} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.title}</span>
          </div>
          <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2 }}>{meta}</div>
        </div>
      </div>

      {parking && (
        <div style={{ padding: "8px 4px 12px", borderBottom: `1px solid ${colors.hair}` }}>
          <input
            autoFocus value={because} onChange={(e) => setBecause(e.target.value)}
            placeholder="why not now" onKeyDown={(e) => e.key === "Escape" && setParking(false)}
            style={{
              width: "100%", boxSizing: "border-box", fontFamily: tokens.font.sans, fontSize: 15,
              padding: "8px 10px", borderRadius: 8, border: `1px solid ${colors.hair2}`,
              background: colors.sheet, color: colors.ink,
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <GlassButton
              variant="primary" disabled={!because.trim()}
              onClick={() => { onPark(because.trim()); setParking(false); setBecause(""); }}
            >Park</GlassButton>
            <GlassButton variant="ghost" onClick={() => { setParking(false); setBecause(""); }}>Cancel</GlassButton>
          </div>
        </div>
      )}
    </div>
  );
}
